import "components/QuerySection/components/OCRStatusList.scss";

import { useEffect, useRef } from "react";
import { api } from "services/api";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import {
  OCR_RATED_AGE_JOB_TYPE,
  RATED_AGE_JOB_POLL_INTERVAL_MS,
  isBackgroundJobProcessing,
} from "utils/backgroundJobStatus";
import { useDispatch } from "utils/redux";

type RatedAgeOCRStatusProps = {
  msaId?: string;
  costProjectionId?: string;
};

// Driven by the BackgroundJob API rather than local state, so every user
// viewing this page sees the same status, not just whoever submitted it.
const RatedAgeOCRStatus = ({
  msaId,
  costProjectionId,
}: RatedAgeOCRStatusProps) => {
  const dispatch = useDispatch();
  const wasProcessing = useRef(false);

  const filters = msaId
    ? { msa_id: msaId }
    : costProjectionId
      ? { cost_projection_id: costProjectionId }
      : undefined;

  const { data: jobsResponse } = useGetBackgroundJobsQuery(
    { filters },
    {
      skip: !filters,
      pollingInterval: RATED_AGE_JOB_POLL_INTERVAL_MS,
    },
  );

  // Results come back newest first, so the first match is the latest job.
  const latestJob = jobsResponse?.results?.find(
    (job) => job.job_type === OCR_RATED_AGE_JOB_TYPE,
  );
  const isProcessing =
    !!latestJob && isBackgroundJobProcessing(latestJob.status);

  // RatedAgeOCRWatcher only invalidates for the submitter's own tab,
  // this catches everyone else once the banner disappears.
  useEffect(() => {
    if (wasProcessing.current && !isProcessing) {
      dispatch(api.util.invalidateTags(["RatedAge", "MSA", "MCP", "LCP"]));
    }
    wasProcessing.current = isProcessing;
  }, [isProcessing, dispatch]);

  if (!isProcessing) {
    return null;
  }

  // Matches OCRStatusList's item styling (purple, pulsing document_scanner
  // icon), so an in-progress rated age job looks the same as every other
  // in-progress OCR job in the app.
  const statusText =
    latestJob.status === "Pending"
      ? "Queued for OCR processing..."
      : "Processing the rated age response document...";

  return (
    <div className="ocr-status-list">
      <div className="data-block">
        <div className="data-block-header">
          <span className="material-icons ocr-status-list__header-icon ocr-status-list__header-icon--pulse">
            document_scanner
          </span>
          Rated Age Response OCR
        </div>
        <div className="ocr-status-list__items">
          <div className="ocr-status-item ocr-status-item--in_progress">
            <div className="ocr-status-item__icon-wrapper">
              <span className="material-icons ocr-status-item__icon ocr-status-item__icon--pulse">
                document_scanner
              </span>
            </div>
            <div className="ocr-status-item__content">
              <div className="ocr-status-item__status-text">{statusText}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatedAgeOCRStatus;
